import { helpers, url } from 'vuelidate/lib/validators';
import { isString, getWordCount, generateParsedDomElementsHandlerDeep, arrayOfObjectsToMapByKey, removeEndingSlashes } from '@fmpedia/helpers';
import validUrl from 'valid-url';
import email from 'vuelidate/lib/validators/email';
import { processHtmlForPayload, parseHtml, checkIfParsedElementIsImage } from '@fmpedia/html-tools';

const PROP_TYPES = {
  BOOLEAN: 'boolean',
  NUMBER: 'number',
  STRING: 'string',
  ARRAY: 'array',
  OBJECT: 'object',
  NODE: 'node',
  FILE: 'file',
  ERROR: 'error',
  ANY: 'any',
  FUNCTION: 'function'
};

const isClientSide = typeof window !== 'undefined';

const TYPE_BY_PROP_TYPE = {
  [PROP_TYPES.ANY]: [],
  [PROP_TYPES.BOOLEAN]: [Boolean],
  [PROP_TYPES.NUMBER]: [Number],
  [PROP_TYPES.STRING]: [String],
  [PROP_TYPES.ARRAY]: [Array],
  [PROP_TYPES.OBJECT]: [Object],
  [PROP_TYPES.NODE]: [isClientSide ? Node : Object],
  [PROP_TYPES.FILE]: [...(isClientSide ? [File, Blob] : [])],
  [PROP_TYPES.ERROR]: [Error],
  [PROP_TYPES.FUNCTION]: [Function]
};

function propValidator(
  propTypes,
  required = true,
  defaultValue,
  validationFunc = () => true
) {
  const propSettings = {
    validator: validationFunc,
    required,
    ...(defaultValue !== undefined ? { default: defaultValue } : {})
  };

  if (propTypes.includes(PROP_TYPES.ANY)) {
    return propSettings
  }

  const types = [
    ...propTypes
      .map(propType => TYPE_BY_PROP_TYPE[propType])
      .filter(v => !!v)
      .reduce((acc, types) => {
        types.forEach(type => {
          acc.add(type);
        });
        return acc
      }, new Set())
  ];

  propSettings.type = types;
  return propSettings
}

/* eslint-disable */
const html = new RegExp('<[a-z][\\s\\S]*>');
/* eslint-enable */

function inputGeneralValidation(value) {
  return !html.test(value)
}

const baseOptionalText = 'Password must contain at least 1 ';
const passwordValidators = [
  {
    check: password => password.length >= 10,
    type: 'required',
    message: 'Password should contain at least 10 characters'
  },
  {
    check: password => password.length <= 128,
    type: 'required',
    message: 'Password should contain less then 128 characters'
  },
  {
    check: password => !/(.)\1{2}/gi.test(password),
    type: 'required',
    message: 'Password cannot contain more than 2 identical characters in a row'
  },
  {
    check: password => /[A-Z]+/g.test(password),
    type: 'optional',
    message: 'uppercase letter'
  },
  {
    check: password => /[a-z]+/g.test(password),
    type: 'optional',
    message: 'lowercase letter'
  },
  {
    check: password => /[0-9]+/g.test(password),
    type: 'optional',
    message: 'digit'
  },
  {
    check: password => /[ !"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]+/g.test(password),
    type: 'optional',
    message: 'special character' // https://www.owasp.org/index.php/Password_special_characters
  }
];

/**
 * Create object with two arrays:
 * - failed validations which are required
 * - failed validations which are optional
 *
 * @param password
 * @returns {{required: Array, optional: Array}}
 */
function getPasswordFailedValidationObject(password) {
  const failedResult = {
    required: [],
    optional: []
  };
  passwordValidators.forEach(rule => {
    if (!rule.check(password)) {
      failedResult[rule.type].push(rule.message);
    }
  });
  return failedResult
}

/**
 * All required validations must be true to pass validation
 * 3 of 4 optional validations must be true to pass validation
 *
 * @param password
 * @returns {boolean}
 */
function isPasswordValid(password) {
  const failedResult = getPasswordFailedValidationObject(password);

  const optionalResultCount = failedResult.optional.length;
  const isRequiredValidationPassed = failedResult.required.length === 0;
  const isOptionalValidationPassed = optionalResultCount <= 1;
  return isRequiredValidationPassed && isOptionalValidationPassed
}

/**
 * This function generates error messages based on validation results.
 * Optional message consists of baseOptionalText and dynamic error messages.
 * Example: Password must contain at least 1 uppercase letter, lowercase letter or special character
 *
 * Reduce method concatenates string according to these rules:
 * 1st dynamic error message should be as is (uppercase letter)
 * last dynamic error message should be "or <message>" (or special character)
 * all other dynamic error messages should be separated by comma ", <message>" (, lowercase letter)
 *
 * @param password
 * @returns {string}  - returns only first error message
 */
function getPasswordValidationMessage(password) {
  const failedResult = getPasswordFailedValidationObject(password);
  const optionalResultCount = failedResult.optional.length;
  const isOptionalValidationPassed = optionalResultCount <= 1;

  const optionalMessage = isOptionalValidationPassed
    ? []
    : [
        baseOptionalText +
          failedResult.optional.reduce((acc, invalidRule, index) => {
            const isFirstRule = index === 0;
            const isLastRule = index === optionalResultCount - 1;

            if (isFirstRule) {
              return `${acc} ${invalidRule}`
            }
            if (isLastRule) {
              return `${acc} or ${invalidRule}`
            }
            return `${acc}, ${invalidRule}`
          }, '')
      ];
  const messageArray = [...failedResult.required, ...optionalMessage];

  return messageArray[0]
}

/**
 * This method is used as validator in Vue components
 * @param password
 * @returns {*}
 */
const password = password => {
  return helpers.withParams({ password }, isPasswordValid)(password)
};

/**
 * Created by Nagibator on 5/10/2017.
 */
function validatePhone(value) {
  if (!value) return true

  const trimmedValue = value.replace(/\s/g, '');

  return /^\+[0-9]{6,16}$/.test(trimmedValue)
}

/**
 * Created by Nagibator on 5/10/2017.
 */
function validatePhone$1(value) {
  if (!value) return false

  const trimmedValue = value.replace(/\s/g, '');

  return /^\+[0-9]{1,5}$/.test(trimmedValue)
}

/* Extended email validation */
function validateEmail(email) {
  const re = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
  return re.test(email)
}

/* Extended HTTPS URL validation */

function validateHttpsUrl(url) {
  if (!url) return true

  if (isString(url) && url.length) {
    const httpsRegex = /^https:\/\/.+/;
    return httpsRegex.test(url)
  }

  return false
}

var anyTruthy = field => collection => {
  if (!Array.isArray(collection)) return false

  return collection.some(item => !!item[field])
};

function isPaginationValid(pagination) {
  return pagination && pagination.take != null && pagination.page != null
}

function replaceLocalhostWithDummyHost(url) {
  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.hostname === 'localhost') {
      parsedUrl.hostname = 'site.com';
    }

    return parsedUrl.toString()
  } catch (err) {
    return url
  }
}

function validateUrl(urlToValidate) {
  if (process.env.NODE_ENV === 'development') {
    urlToValidate = replaceLocalhostWithDummyHost(urlToValidate);
  }

  return url(urlToValidate)
}

const minWordCount = wordCount => val => getWordCount(val) >= wordCount;

const hrefValidation = {
  handledCase: {
    isStartFromMailto: 'isStartFromMailto',
    isStartFromProtocol: 'isStartFromProtocol',
    isStartFromDoubleSlash: 'isStartFromDoubleSlash',
    isStartFromSingleSlash: 'isStartFromSingleSlash',
    isStartFromHash: 'isStartFromHash'
  },
  mockedProtocol: 'https',
  mockedDomain: 'domain.com',
  get regexpStringByCase() {
    return {
      [this.handledCase.isStartFromMailto]: '^mailto:.+',
      [this.handledCase.isStartFromProtocol]: '^[\\w\\-_]+:\\/\\/.+',
      [this.handledCase.isStartFromDoubleSlash]: '^//[^/]',
      [this.handledCase.isStartFromSingleSlash]: '^/[^/]',
      [this.handledCase.isStartFromHash]: '^#[^#]?'
    }
  },
  get handlerByCase() {
    return {
      [this.handledCase.isStartFromMailto]:
        this.validateIfStartsWithMailto.bind(this),
      [this.handledCase.isStartFromProtocol]:
        this.validateIfStartsWithProtocol.bind(this),
      [this.handledCase.isStartFromDoubleSlash]:
        this.validateIfStartsWithDoubleSlash.bind(this),
      [this.handledCase.isStartFromSingleSlash]:
        this.validateIfStartsWithSingleSlash.bind(this),
      [this.handledCase.isStartFromHash]:
        this.validateIfStartsWithHash.bind(this)
    }
  },
  validateIfStartsWithMailto(href) {
    const emailAddress = href.match(/mailto:(.+)/i)[1];

    return email(emailAddress)
  },
  validateIfStartsWithProtocol(href) {
    return !!validUrl.isUri(href)
  },
  validateIfStartsWithDoubleSlash(href) {
    const urlWithMockedProtocol = href.replace(
      /^\/\//i,
      `${this.mockedProtocol}://`
    );

    return this.validateIfStartsWithProtocol(urlWithMockedProtocol)
  },
  validateIfStartsWithSingleSlash(href) {
    const urlWithMockedProtocol = href.replace(
      /^\//i,
      `${this.mockedProtocol}://`
    );

    return this.validateIfStartsWithProtocol(urlWithMockedProtocol)
  },
  validateIfStartsWithHash(href) {
    const urlWithMockedPath = `${this.mockedProtocol}://${this.mockedDomain}${href}`;

    return this.validateIfStartsWithProtocol(urlWithMockedPath)
  },
  isHrefValid(href) {
    if (!href) return false

    const hrefCase = Object.values(this.handledCase).find(handledCase => {
      const caseRegexp = new RegExp(this.regexpStringByCase[handledCase], 'si');

      return href.match(caseRegexp)
    });

    if (!hrefCase) return false

    const handler = this.handlerByCase[hrefCase];

    try {
      return handler(href)
    } catch (err) {
      return false
    }
  }
};

function isHrefValid(href) {
  return hrefValidation.isHrefValid(href)
}

const YOUTUBE_URL = {
  BASE: '(https?:\\/\\/)?(www\\.)?(youtube\\.com|youtu\\.?be)\\/[^\\s\\n\\r]+',
  get URL_REGEXP() {
    return new RegExp(`^${this.BASE}$`)
  },
  get URL_IN_TEXT_REGEXP() {
    return new RegExp(this.BASE, 'gm')
  }
};

const YOUTUBE_ID_LENGTH = 11;

function isYoutubeUrl(url) {
  return YOUTUBE_URL.URL_REGEXP.test(url)
}

function getYoutubeVideoId(url) {
  const regExp =
    /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  const id = (match && match[2]) || '';

  return id.length === YOUTUBE_ID_LENGTH ? id : null
}

function isValidYoutubeVideoUrl(url) {
  const isYoutubeLink = isYoutubeUrl(url);
  if (!isYoutubeLink) return false

  const youtubeVideoId = getYoutubeVideoId(url);
  return isYoutubeLink && !!youtubeVideoId
}

function isYoutubeUrlValidator(url) {
  if (!url) return true

  if (isString(url) && url.length) {
    return isValidYoutubeVideoUrl(url)
  }

  return false
}

function requiredHtml(html) {
  if (!html) return false

  return !!processHtmlForPayload(html)
}

const IMAGE_BASE_64_REGEXP = /^data:image\/.+;base64,/;

function checkIfParsedElementIsBase64Image(el) {
  return (
    checkIfParsedElementIsImage(el) &&
    ((el.attrs && el.attrs.src) || '').match(IMAGE_BASE_64_REGEXP)
  )
}

function htmlHasBase64Images(html) {
  let hasBase64Image = false;

  const handlerSettings = [
    {
      conditionFn: el => {
        if (hasBase64Image) return

        return checkIfParsedElementIsBase64Image(el)
      },
      handlerFn: img => {
        hasBase64Image = true;

        return img
      }
    }
  ];

  generateParsedDomElementsHandlerDeep(handlerSettings)(parseHtml(html));

  return hasBase64Image
}

function validImagesInHtml(html) {
  return !htmlHasBase64Images(html)
}

const ATTRIBUTES_TO_IGNORE = [
  'class',
  'style',
  'v-if',
  'v-else',
  'v-else-if',
  'v-on',
  'v-for',
  'v-bind',
  'key',
  'in',
  'data-ref',
  'ref',
  'slot'
];

function findVueFiles({ dir, fileList = [], rootDir, fs, path }) {
  try {
    const relativeDir = dir.replace(rootDir, '');
    const regexpString =
      '(^/node_modules/)|(/app/)|(/\\.nuxt)|(\\.git)|(/caching-app/)|(/\\.git/)|(/\\.idea)';
    const validationRegexp = new RegExp(regexpString);

    if (relativeDir.match(validationRegexp)) {
      return fileList
    }
    const files = fs.readdirSync(dir);

    files.forEach(file => {
      const filePath = path.join(dir, file);
      const stat = fs.statSync(filePath);

      if (stat.isDirectory()) {
        findVueFiles({ dir: filePath, fileList, rootDir, fs, path });
      } else if (filePath.endsWith('.vue')) {
        fileList.push(filePath);
      }
    });
  } catch (error) {
    console.error(`Error reading directory ${dir}:`, error);
  }

  return fileList
}

function readVueFilesContent(filePaths, fs) {
  filePaths.forEach(filePath => {
    const content = fs.readFileSync(filePath, 'utf8');
    inspectVueFileContent(content, filePath);
  });
}

function getComponentsSection(scriptString) {
  const regexpString = 'components: {';
  const regexp = new RegExp(regexpString);
  const match = scriptString.match(regexp);

  if (!match) return null

  const index = match.index;
  let bracketCounter = 0;
  let isOpenBrackedFound = false;
  let indexCounter = index;
  while (
    indexCounter <= scriptString.length &&
    (!isOpenBrackedFound || bracketCounter > 0)
  ) {
    const currentChar = scriptString[indexCounter];
    switch (currentChar) {
      case '{':
        isOpenBrackedFound = true;
        bracketCounter++;
        indexCounter++;
        break
      case '}':
        bracketCounter--;
        indexCounter++;
        break
      default:
        indexCounter++;
    }
  }

  return scriptString.slice(index, indexCounter)
}

function inspectVueFileContent(content, filePath) {
  if (!content) return

  const match = content.match(
    /<template>([\w\W]+)<\/template>[\w\W]+<script>([\w\W]+)<\/script>/
  );

  if (!match) return

  const template = match[1];
  const script = match[2];

  const componentsSection = getComponentsSection(script);

  if (!componentsSection) return

  const componentsData = getHydratedComponentNamesFromScript(componentsSection);
  // console.log('componentsData: ', componentsData)
  const hydratedComponentsData = componentsData.filter(
    ({ isHydrated }) => isHydrated
  );
  // console.log('hydratedComponentsData: ', hydratedComponentsData)

  const openTagsRegexpString = '<[^>]+?>';
  const openTagsRegexp = new RegExp(openTagsRegexpString, 'g');
  const openTags = template.match(openTagsRegexp);
  if (!openTags) return

  // console.log('openTags: ', openTags)
  const tagsData = openTags.map(parseOpenTagString);
  // console.log('tagsData: ', tagsData)

  const tagsDataByTag = arrayOfObjectsToMapByKey({
    arrayOfObjects: tagsData.filter(v => !!v),
    keyName: 'tagName'
  });
  // console.log('tagsDataByTag: ', tagsDataByTag)
  hydratedComponentsData.forEach(componentData => {
    const { componentName, props } = componentData;
    // console.log('componentName: ', componentName)

    const nameInTemplate = componentName
      .split('')
      .map((char, index) => {
        if (index === 0) return char.toLowerCase()

        return isUpperCase(char) ? `-${char.toLowerCase()}` : char
      })
      .join('');
    // console.log('nameInTemplate: ', nameInTemplate)

    const foundTagData = tagsDataByTag[nameInTemplate];
    if (!foundTagData) {
      const reset = '\x1b[0m';
      const red = '\x1b[31m';
      console.log('-----');
      console.log('filePath: ', filePath);
      console.log(
        `HTML tag ${red}${nameInTemplate}${reset} was not found in the template for the component ${componentName}`
      );
      return
    }

    // const attributesRegexpString = '[\\s:]\\b(.+?)\\b[=\\s]'
    // const attributesRegexpString = '[\\s:]\\b([\\w\\-]+?)\\b(\\.\\w+)?[=\\s]'
    const attributesRegexpString = '[\\s:]\\b([\\w\\-]+?)\\b(\\.\\w+)?(=".+")';
    const attributesRegexp = new RegExp(attributesRegexpString, 'gm');
    const attributesMatch =
      foundTagData.attributesString.match(attributesRegexp);

    if (!attributesMatch) {
      return
    }

    const attributes = attributesMatch.map(attributeString => {
      const attributeRegexp = new RegExp(attributesRegexpString, 'i');
      const attributeMatch = attributeString.match(attributeRegexp);

      return attributeMatch[1]
    });

    const attributesToCheck = attributes.filter(
      attribute => !ATTRIBUTES_TO_IGNORE.includes(attribute)
    );

    const warningAttributes = attributesToCheck.filter(
      attribute => !(props || []).includes(attribute)
    );
    // console.log('props: ', props)
    // console.log('attributes: ', attributes)
    // console.log('warningAttributes: ', warningAttributes)
    if (!warningAttributes.length) return

    warningAttributes.forEach(attribute => {
      console.log('-----');
      console.log('filePath: ', filePath);
      const reset = '\x1b[0m';
      const red = '\x1b[31m';
      const cyan = '\x1b[36m';
      console.log('component name: ', cyan, componentName, reset);
      console.log(
        'component name in the template: ',
        cyan,
        nameInTemplate,
        reset
      );
      process.stdout.write(
        `Attribute ${red}${attribute}${reset} not found in the component props.\n`
      );
      console.log('  > component props in the script: ', props);
      console.log('  > tag attributes in the template: ', attributesToCheck);
      console.log('\n');
    });
  });
}

function isUpperCase(char) {
  return char === char.toUpperCase() && char !== char.toLowerCase()
}

function parseOpenTagString(openTag) {
  if (!openTag) return

  const regexpString = '<([\\w-]+)[\\s\\n\\r]*?([\\w\\W]*)>';
  const regexp = new RegExp(regexpString, 'i');
  const match = openTag.match(regexp);

  if (!match) return

  const tagName = match[1];
  const attributesString = match[2];

  // console.log('tagName: ', tagName)
  // console.log('attributesString: ', attributesString)
  return { tagName, attributesString }
}

function getComponentString(str, debug = false) {
  let currentIndex = 0;
  let braceCounter = 0; // {}
  let parenthesisCounter = 0; // ()
  let bracketCounter = 0; // []
  while (
    currentIndex <= str.length &&
    (str[currentIndex] !== ',' ||
      braceCounter !== 0 ||
      parenthesisCounter !== 0 ||
      bracketCounter !== 0)
  ) {
    switch (str[currentIndex]) {
      case '{':
        braceCounter++;
        break
      case '}':
        braceCounter--;
        break
      case '(':
        parenthesisCounter++;
        break
      case ')':
        parenthesisCounter--;
        break
      case '[':
        bracketCounter++;
        break
      case ']':
        bracketCounter--;
        break
    }
    currentIndex++;
  }

  return {
    componentString: str.slice(0, currentIndex),
    currentIndex
  }
}

function getComponentsArray(componentsString) {
  const openBracketMatch = componentsString.match('{');

  if (!openBracketMatch) return null

  let stringToProcess = componentsString.slice(openBracketMatch.index + 1);
  let counter = 0;
  const parsedComponents = [];
  do {
    const { componentString, currentIndex } = getComponentString(
      stringToProcess,
      counter === 1
    );
    stringToProcess = stringToProcess.slice(currentIndex + 1);
    parsedComponents.push(componentString);
    counter++;
  } while (stringToProcess.length)

  return parsedComponents
}

function getHydratedComponentNamesFromScript(script) {
  if (!script) return

  const componentsArray = getComponentsArray(script);

  if (!componentsArray.length) return

  const componentsWithProps = componentsArray
    .map(componentNameString => {
      const propsListRegexpString = `\\bprops: \\[([\\w\\W]+)]`;
      const propsListRegexp = new RegExp(propsListRegexpString, 'i');
      const propsListMatch = componentNameString.match(propsListRegexp);

      const componentNameRegexpString = '\\b([a-zA-Z0-9-_]*)\\b';
      const componentNameRegexp = new RegExp(componentNameRegexpString, 'i');
      const componentNameMatch = componentNameString.match(componentNameRegexp);

      const componentName = componentNameMatch
        ? componentNameMatch[1].trim()
        : 'unknown';

      const isHydrated = ['hydrateWhenVisible', 'hydrateWhenIdle'].some(
        searchString => componentNameString.includes(searchString)
      );

      if (!propsListMatch) return { componentName, props: null, isHydrated }

      const props = propsListMatch[1]
        .split(',')
        .map(rawProp => rawProp.replace(/['"]/g, '').trim());

      return { componentName, props, isHydrated }
    })
    .filter(v => !!v);

  return componentsWithProps
}

function validateVueFiles({ fs, path }) {
  const rootDir = path.resolve('./');
  const vueFiles = findVueFiles({
    dir: rootDir,
    fileList: [],
    rootDir,
    fs,
    path
  });
  // console.log('vueFiles: ', vueFiles)
  // const mockedPath =
  //   '/home/khlopik/Work/Projects/financemagnets/components/_sections/terms/ASuggestATerm/index.vue'
  // readVueFilesContent([mockedPath])
  console.log('Total Vue files: ', vueFiles.length);
  readVueFilesContent(vueFiles, fs);
}

const redirectValidation = {
  redirectData: [],
  getRedirectSettings: () => null,
  assignRedirectData(redirectData) {
    this.redirectData = redirectData;
  },
  assignGetRedirectSettingsFn(getRedirectSettings) {
    this.getRedirectSettings = getRedirectSettings;
  },
  findUnnecessaryRedirects() {
    console.log('>> Testing for unnecessary redirects...');
    const redirectMap = new Map();

    this.redirectData.forEach(({ from, to }) => {
      const processedFrom = removeEndingSlashes(from.toLowerCase());
      const processedTo = removeEndingSlashes(to.toLowerCase());
      redirectMap.set(processedFrom, processedTo);
    });

    const unnecessaryRedirects = [];

    for (const [source, target] of redirectMap.entries()) {
      const processedTarget = removeEndingSlashes(target.toLowerCase());
      const intermediate = redirectMap.get(processedTarget);
      if (intermediate) {
        unnecessaryRedirects.push({ from: source, intermediate, to: target });
      }
    }

    if (unnecessaryRedirects.length) {
      console.log('Failed');
      console.log('Unnecessary redirect data: ', unnecessaryRedirects);
    } else {
      console.log('Passed');
    }
  },
  async getLoopFoundData(fromUrl, redirectCount = 0, redirectUrls = []) {
    const redirectToSettings = await this.getRedirectSettings(fromUrl);
    if (!redirectToSettings) return null

    const { to } = redirectToSettings;
    if (redirectUrls.includes(fromUrl) || redirectCount >= 5) {
      return {
        from: fromUrl,
        to,
        redirectUrls,
        redirectCount
      }
    }
    return this.getLoopFoundData(to, redirectCount + 1, [
      ...redirectUrls,
      fromUrl
    ])
  },
  async validateAll() {
    await this.checkRedirectLoops();
    this.checkDuplicateFromUrls();
    this.checkDuplicateFromToUrls();
    this.findUnnecessaryRedirects();
  },
  async checkRedirectLoops() {
    console.log('>> Testing for redirect loops...');
    if (!this.getRedirectSettings) {
      console.log('Failed. Function getRedirectSettings is not provided!');
      return null
    }

    const redirectLoops = await Promise.all(
      this.redirectData.map(redirect => {
        const { from: sourceFrom } = redirect;
        const loopFoundData = this.getLoopFoundData(sourceFrom);

        if (!loopFoundData) return null

        return loopFoundData
      })
    );
    const filteredLoops = redirectLoops.filter(v => !!v);
    if (filteredLoops.length) {
      console.log('Failed');
      console.log('Redirect loop data: ', filteredLoops);
    } else {
      console.log('Passed');
    }
  },
  checkDuplicateFromUrls() {
    console.log('>> Testing for duplicate From URLs...');
    const redirectToUrlsByFromUrl = this.redirectData.reduce(
      (acc, { from, to }) => {
        const formattedFrom = removeEndingSlashes(from.toLowerCase());
        const arrayOfTo = acc[formattedFrom] || [];
        arrayOfTo.push(to);
        return {
          ...acc,
          [formattedFrom]: arrayOfTo
        }
      },
      {}
    );
    const duplicateFromRedirects = Object.entries(
      redirectToUrlsByFromUrl
    ).reduce((acc, [from, arrayOfToUrls]) => {
      if (arrayOfToUrls.length <= 1) return acc

      return [...acc, { from, arrayOfToUrls }]
    }, []);
    if (duplicateFromRedirects.length) {
      console.log('Failed');
      console.log('Duplicate From URL redirect data: ', duplicateFromRedirects);
    } else {
      console.log('Passed');
    }
  },
  checkDuplicateFromToUrls() {
    console.log('>> Testing for same From/To URLs...');
    const duplicateUrls = this.redirectData.filter(
      ({ from, to }) =>
        removeEndingSlashes(from).toLowerCase() ===
        removeEndingSlashes(to).toLowerCase()
    );
    if (duplicateUrls.length) {
      console.log('Failed');
      console.log('Same From/To URLs redirect data', duplicateUrls);
    } else {
      console.log('Passed');
    }
  }
};

function validateDecimalNumeric(value) {
  return !isNaN(parseFloat(value)) && isFinite(value) && value >= 0
}

function validateDecimalNumeric$1(value) {
  return !isNaN(parseFloat(value)) && isFinite(value) && value > 0
}

function _isPlaceholder(a) {
       return a != null && typeof a === 'object' && a['@@functional/placeholder'] === true;
}

/**
 * Optimized internal one-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */
function _curry1(fn) {
  return function f1(a) {
    if (arguments.length === 0 || _isPlaceholder(a)) {
      return f1;
    } else {
      return fn.apply(this, arguments);
    }
  };
}

/**
 * Optimized internal two-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */
function _curry2(fn) {
  return function f2(a, b) {
    switch (arguments.length) {
      case 0:
        return f2;
      case 1:
        return _isPlaceholder(a) ? f2 : _curry1(function (_b) {
          return fn(a, _b);
        });
      default:
        return _isPlaceholder(a) && _isPlaceholder(b) ? f2 : _isPlaceholder(a) ? _curry1(function (_a) {
          return fn(_a, b);
        }) : _isPlaceholder(b) ? _curry1(function (_b) {
          return fn(a, _b);
        }) : fn(a, b);
    }
  };
}

/**
 * Optimized internal three-arity curry function.
 *
 * @private
 * @category Function
 * @param {Function} fn The function to curry.
 * @return {Function} The curried function.
 */
function _curry3(fn) {
  return function f3(a, b, c) {
    switch (arguments.length) {
      case 0:
        return f3;
      case 1:
        return _isPlaceholder(a) ? f3 : _curry2(function (_b, _c) {
          return fn(a, _b, _c);
        });
      case 2:
        return _isPlaceholder(a) && _isPlaceholder(b) ? f3 : _isPlaceholder(a) ? _curry2(function (_a, _c) {
          return fn(_a, b, _c);
        }) : _isPlaceholder(b) ? _curry2(function (_b, _c) {
          return fn(a, _b, _c);
        }) : _curry1(function (_c) {
          return fn(a, b, _c);
        });
      default:
        return _isPlaceholder(a) && _isPlaceholder(b) && _isPlaceholder(c) ? f3 : _isPlaceholder(a) && _isPlaceholder(b) ? _curry2(function (_a, _b) {
          return fn(_a, _b, c);
        }) : _isPlaceholder(a) && _isPlaceholder(c) ? _curry2(function (_a, _c) {
          return fn(_a, b, _c);
        }) : _isPlaceholder(b) && _isPlaceholder(c) ? _curry2(function (_b, _c) {
          return fn(a, _b, _c);
        }) : _isPlaceholder(a) ? _curry1(function (_a) {
          return fn(_a, b, c);
        }) : _isPlaceholder(b) ? _curry1(function (_b) {
          return fn(a, _b, c);
        }) : _isPlaceholder(c) ? _curry1(function (_c) {
          return fn(a, b, _c);
        }) : fn(a, b, c);
    }
  };
}

/**
 * Retrieve the value at a given path.
 *
 * @func
 * @memberOf R
 * @since v0.2.0
 * @category Object
 * @typedefn Idx = String | Int
 * @sig [Idx] -> {a} -> a | Undefined
 * @param {Array} path The path to use.
 * @param {Object} obj The object to retrieve the nested property from.
 * @return {*} The data at `path`.
 * @see R.prop
 * @example
 *
 *      R.path(['a', 'b'], {a: {b: 2}}); //=> 2
 *      R.path(['a', 'b'], {c: {b: 2}}); //=> undefined
 */
var path = /*#__PURE__*/_curry2(function path(paths, obj) {
  var val = obj;
  var idx = 0;
  while (idx < paths.length) {
    if (val == null) {
      return;
    }
    val = val[paths[idx]];
    idx += 1;
  }
  return val;
});

/**
 * Returns the second argument if it is not `null`, `undefined` or `NaN`;
 * otherwise the first argument is returned.
 *
 * @func
 * @memberOf R
 * @since v0.10.0
 * @category Logic
 * @sig a -> b -> a | b
 * @param {a} default The default value.
 * @param {b} val `val` will be returned instead of `default` unless `val` is `null`, `undefined` or `NaN`.
 * @return {*} The second value if it is not `null`, `undefined` or `NaN`, otherwise the default value
 * @example
 *
 *      var defaultTo42 = R.defaultTo(42);
 *
 *      defaultTo42(null);  //=> 42
 *      defaultTo42(undefined);  //=> 42
 *      defaultTo42('Ramda');  //=> 'Ramda'
 *      // parseInt('string') results in NaN
 *      defaultTo42(parseInt('string')); //=> 42
 */
var defaultTo = /*#__PURE__*/_curry2(function defaultTo(d, v) {
  return v == null || v !== v ? d : v;
});

/**
 * If the given, non-null object has a value at the given path, returns the
 * value at that path. Otherwise returns the provided default value.
 *
 * @func
 * @memberOf R
 * @since v0.18.0
 * @category Object
 * @typedefn Idx = String | Int
 * @sig a -> [Idx] -> {a} -> a
 * @param {*} d The default value.
 * @param {Array} p The path to use.
 * @param {Object} obj The object to retrieve the nested property from.
 * @return {*} The data at `path` of the supplied object or the default value.
 * @example
 *
 *      R.pathOr('N/A', ['a', 'b'], {a: {b: 2}}); //=> 2
 *      R.pathOr('N/A', ['a', 'b'], {c: {b: 2}}); //=> "N/A"
 */
var pathOr = /*#__PURE__*/_curry3(function pathOr(d, p, obj) {
  return defaultTo(d, path(p, obj));
});

/**
 * ============================================================================
 * GENERAL CONSTS
 * ============================================================================
 */

const CUSTOM_HTML_TAG = {
  A_SCRIPT: 'a-script',
  A_IFRAME: 'a-iframe',
  A_IMAGE: 'a-image',
  A_LINK: 'a-link',
  A_ENLARGEABLE_IMAGE: 'a-enlargeable-image',
  A_TABLE_INSIDE_ARTICLE: 'a-table-inside-article'
};

const CUSTOM_HTML_TAG_VALUES = Object.values(CUSTOM_HTML_TAG);

const REL_ATTRIBUTE_BACKEND_ENUM = {
  NONE: 'None',
  ALTERNATE: 'Alternate',
  AUTHOR: 'Author',
  FOLLOW: 'Follow',
  NOFOLLOW: 'Nofollow',
  BOOKMARK: 'Bookmark',
  EXTERNAL: 'External',
  HELP: 'Help',
  LICENSE: 'License',
  NEXT: 'Next',
  NOOPENER: 'Noopener',
  NOREFERRER: 'Noreferrer',
  PREV: 'Prev',
  SEARCH: 'Search',
  TAG: 'Tag'
};

const HTML_REL_ATTRIBUTE_VALUE = {
  ALTERNATE: 'alternate',
  AUTHOR: 'author',
  NOFOLLOW: 'nofollow',
  BOOKMARK: 'bookmark',
  EXTERNAL: 'external',
  HELP: 'help',
  LICENSE: 'license',
  NEXT: 'next',
  NOOPENER: 'noopener',
  NOREFERRER: 'noreferrer',
  PREV: 'prev',
  SEARCH: 'search',
  TAG: 'tag'
};

const HTML_REL_ATTRIBUTE_VALUE_BY_BACKEND_ENUM = {
  [REL_ATTRIBUTE_BACKEND_ENUM.NONE]: '',
  [REL_ATTRIBUTE_BACKEND_ENUM.ALTERNATE]: HTML_REL_ATTRIBUTE_VALUE.ALTERNATE,
  [REL_ATTRIBUTE_BACKEND_ENUM.AUTHOR]: HTML_REL_ATTRIBUTE_VALUE.AUTHOR,
  [REL_ATTRIBUTE_BACKEND_ENUM.FOLLOW]: '',
  [REL_ATTRIBUTE_BACKEND_ENUM.NOFOLLOW]: HTML_REL_ATTRIBUTE_VALUE.NOFOLLOW,
  [REL_ATTRIBUTE_BACKEND_ENUM.BOOKMARK]: HTML_REL_ATTRIBUTE_VALUE.BOOKMARK,
  [REL_ATTRIBUTE_BACKEND_ENUM.EXTERNAL]: HTML_REL_ATTRIBUTE_VALUE.EXTERNAL,
  [REL_ATTRIBUTE_BACKEND_ENUM.HELP]: HTML_REL_ATTRIBUTE_VALUE.HELP,
  [REL_ATTRIBUTE_BACKEND_ENUM.LICENSE]: HTML_REL_ATTRIBUTE_VALUE.LICENSE,
  [REL_ATTRIBUTE_BACKEND_ENUM.NEXT]: HTML_REL_ATTRIBUTE_VALUE.NEXT,
  [REL_ATTRIBUTE_BACKEND_ENUM.NOOPENER]: HTML_REL_ATTRIBUTE_VALUE.NOOPENER,
  [REL_ATTRIBUTE_BACKEND_ENUM.NOREFERRER]: HTML_REL_ATTRIBUTE_VALUE.NOREFERRER,
  [REL_ATTRIBUTE_BACKEND_ENUM.PREV]: HTML_REL_ATTRIBUTE_VALUE.PREV,
  [REL_ATTRIBUTE_BACKEND_ENUM.SEARCH]: HTML_REL_ATTRIBUTE_VALUE.SEARCH,
  [REL_ATTRIBUTE_BACKEND_ENUM.TAG]: HTML_REL_ATTRIBUTE_VALUE.TAG
};

const BACKEND_ENUM_BY_HTML_REL_ATTRIBUTE_VALUE = Object.entries(
  HTML_REL_ATTRIBUTE_VALUE_BY_BACKEND_ENUM
)
  .filter(([, value]) => value)
  .reduce((accumulator, [key, value]) => {
    accumulator[value] = key;
    return accumulator
  }, {});

const MEDIA_TYPE = {
  IMAGE: 'Image',
  VIDEO: 'Video',
  PDF: 'PDF',
  MS_WORD: 'MsWord',
  MS_EXCEL: 'MsExcel',
  CSV: 'Other',
  UNKNOWN: 'Unknown'
};

const FILE_EXTENSION = {
  PNG: '.png',
  JPEG: '.jpeg',
  JPG: '.jpg',
  GIF: '.gif',
  MP4: '.mp4',
  PDF: '.pdf',
  DOC: '.doc',
  DOCX: '.docx',
  XLS: '.xls',
  XLSX: '.xlsx',
  CSV: '.csv'
};

const MEDIA_TYPE_FORMATS = {
  [MEDIA_TYPE.IMAGE]: (isOld) => {
    if (isOld) {
      return [
        FILE_EXTENSION.JPEG,
        FILE_EXTENSION.JPG,
        FILE_EXTENSION.GIF,
        FILE_EXTENSION.PNG
      ]
    } else {
      return [FILE_EXTENSION.JPEG, FILE_EXTENSION.JPG, FILE_EXTENSION.GIF]
    }
  },
  [MEDIA_TYPE.VIDEO]: () => ['.mp4'],
  [MEDIA_TYPE.PDF]: () => [FILE_EXTENSION.PDF],
  [MEDIA_TYPE.MS_WORD]: () => [FILE_EXTENSION.DOC, FILE_EXTENSION.DOCX],
  [MEDIA_TYPE.MS_EXCEL]: () => [FILE_EXTENSION.XLS, FILE_EXTENSION.XLSX],
  [MEDIA_TYPE.CSV]: () => [FILE_EXTENSION.CSV]
};

function fileExtensionValidator(file, mediaType) {
  if (!file) return true

  const extension = this.$helper.getFileExtension(pathOr('', ['name'], file));
  return MEDIA_TYPE_FORMATS[mediaType](file.isOld).includes(extension)
}

function pdfValidator(file) {
  return fileExtensionValidator.call(this, file, MEDIA_TYPE.PDF)
}

function imageValidator(file) {
  return fileExtensionValidator.call(this, file, MEDIA_TYPE.IMAGE)
}

function noSlashes(str = '') {
  try {
    return !str.includes('/') && !str.includes('\\')
  } catch (e) {
    return false
  }
}

/* eslint-disable */
const HTTP_LINK_TAG_REGEXP = new RegExp(
  '<a[^>]+?href="(http://.+?)".+?>',
  'gim'
);
const HTTP_LINK_HREF_REGEXP = new RegExp('href="(?<link>.+?)"', 'i');

function validateHttpsInsideHtml(html) {
  if (!html) return true

  const aTags = html.match(HTTP_LINK_TAG_REGEXP);
  if (!aTags) return true

  return aTags.every(linkTag => {
    const href = linkTag.match(HTTP_LINK_HREF_REGEXP).groups.link;
    if (!href) return true

    return isBackofficeLink(href)
  })
}

function isBackofficeLink(link) {
  try {
    const { origin } = new URL(link);

    return origin === process.env.DOMAIN_URL
  } catch (err) {
    console.error(err);
    return false
  }
}
/* eslint-enable */

export { PROP_TYPES, YOUTUBE_URL, anyTruthy, validateDecimalNumeric$1 as decimalGreaterThanZero, validateDecimalNumeric as decimalNumeric, inputGeneralValidation as generalValidation, getPasswordValidationMessage, validateHttpsUrl as https, validateHttpsInsideHtml as httpsInsideHtml, imageValidator, isHrefValid, isPaginationValid, isValidYoutubeVideoUrl, isYoutubeUrlValidator, minWordCount, noSlashes, password, pdfValidator, validatePhone as phone, validatePhone$1 as phoneCode, propValidator, redirectValidation, requiredHtml, validImagesInHtml, validateEmail, validateUrl, validateVueFiles };
